/**
 * Global formatting utilities for consistent display across the application
 */

/**
 * Format a number as USD currency
 * @param {number|null} amount - The amount to format
 * @param {string} fallback - Value to return if amount is null/undefined (default: '$0.00')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, fallback = '$0.00') => {
  if (amount == null) return fallback;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

/**
 * Format a date string for display (MM/DD/YYYY)
 * Treats YYYY-MM-DD as a local date (no timezone shift)
 * @param {string|number|Date} dateValue - Date string, timestamp, or Date object
 * @param {string} fallback - Value to return if date is invalid (default: 'N/A')
 * @returns {string} Formatted date string
 */
export const formatDate = (dateValue, fallback = 'N/A') => {
  if (!dateValue) return fallback;

  try {
    // Handle LocalDate-style strings safely
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const [year, month, day] = dateValue.split('-');
      return new Date(year, month - 1, day)
        .toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        });
    }

    // Fallback for timestamps / Date objects
    return new Date(dateValue).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  } catch {
    return fallback;
  }
};
/**
 * Convert date from yyyy-MM-dd to MM/dd/yyyy format for API calls
 * @param {string} dateStr - Date string in yyyy-MM-dd format
 * @returns {string} Date string in MM/dd/yyyy format
 */
export const formatDateForApi = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${month}/${day}/${year}`;
};

/**
 * Format a number as a percentage
 * @param {number|null} value - The percentage value
 * @param {string} fallback - Value to return if value is null/undefined (default: '-')
 * @returns {string} Formatted percentage string with % symbol
 */
export const formatPercent = (value, fallback = '-') => {
  if (value == null) return fallback;
  return `${value}%`;
};

/**
 * Format a number with locale-specific formatting (no currency symbol)
 * @param {number|null} value - The number to format
 * @param {number} minimumFractionDigits - Minimum decimal places (default: 0)
 * @param {number} maximumFractionDigits - Maximum decimal places (default: 2)
 * @param {string} fallback - Value to return if value is null/undefined (default: '-')
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, minimumFractionDigits = 0, maximumFractionDigits = 2, fallback = '') => {
  if (value == null) return fallback;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping: false,
  }).format(value);
};

/**
 * Format a number as USD currency with no decimal places (whole dollars)
 * @param {number|null} value - The amount to format
 * @param {string} fallback - Value to return if value is null/undefined (default: '-')
 * @returns {string} Formatted currency string without cents
 */
export const formatCurrencyWhole = (value, fallback = '-') => {
  if (value == null) return fallback;
  return '$' + Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

/**
 * Format address components into a single string
 * Handles various field naming conventions (street/address, zip/postal)
 * @param {Object} addressParts - Object containing address components
 * @param {string} fallback - Value to return if no address parts (default: 'N/A')
 * @returns {string} Formatted address string
 */
export const formatAddress = (addressParts, fallback = 'N/A') => {
  if (!addressParts) return fallback;

  const street = addressParts.street || addressParts.address || '';
  const city = addressParts.city || '';
  const state = addressParts.state || '';
  const zip = addressParts.zip || addressParts.postal || '';

  const parts = [street, city, state].filter(Boolean);
  if (parts.length === 0) return fallback;

  // Format as "Street, City, State ZIP"
  let result = parts.join(', ');
  if (zip) result += ` ${zip}`;
  return result;
};

/**
 * Format a phone number to (XXX) XXX-XXXX format
 * @param {string} phone - Phone number (digits only or formatted)
 * @param {string} fallback - Value to return if phone is invalid (default: 'N/A')
 * @returns {string} Formatted phone number
 */
export const formatPhone = (phone, fallback = 'N/A') => {
  if (!phone) return fallback;

  // Remove all non-digits
  const digits = String(phone).replace(/\D/g, '');

  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // Return original if not 10 digits
  return phone || fallback;
};

export const formatTier = (state, tier) => {
  const character = state === 'OR'? 'G': 'T';
  return `${state}-${character}${tier}`;
}